import { Accordion, Button, Dialog, DialogActions, DialogContent, Grid2, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Select from '../../../../components/forms/Select'
import { nbPerPageLabel } from '../../../../referencial/constants/ReferencialConstants'
import { getParametersNature, getParametersType } from '../../../../referencial/util/ReferencialUtils'
import { getStatusInformation } from '../../../../referencial/util/StatusUtils'
import { getLocalizationPicto } from '../../../../utils/AnalyseUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useListIndexed from 'utils/customHook/useListIndexed'
import useSandreList from 'utils/customHook/useSandreList'
import { isNil, compact } from 'lodash'
import useBoolean from 'utils/customHook/useBoolean'
import { StyledFieldSet } from 'components/StyledElements'
import { NewTable } from 'components/datatable/NewTable'
import { AccordionTitle } from 'components/styled/Accordions'
import { getI18nTitleDataLength } from 'utils/StringUtil'

const headers = ['code', 'name', 'type', 'nature', 'status', 'unit', 'fraction', 'support', 'place']

const UpdatePopin = ({
    isOpen = false,
    onClose = () => { },
    onValidate = () => { },

    fields = {},
}) => {
    const {
        parameters,
        units,
        fractions,
        supports,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const analysisPlaces = useSandreList('ANALYSES.LIEU')

    const indexedParameters = useListIndexed(parameters, 'code')

    const [unit, setUnit] = useState()
    const [fraction, setFraction] = useState()
    const [support, setSupport] = useState()
    const [place, setPlace] = useState()

    useEffect(() => {
        if (!isOpen) return

        setUnit(fields.unit)
        setFraction(fields.fraction)
        setSupport(fields.support)
        setPlace(fields.place)
    }, [isOpen])

    return (
        <Dialog
            onClose={onClose}
            fullWidth
            maxWidth='md'
            open={isOpen}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: undefined,
                        maxHeight: undefined,
                    },
                },
            }}
        >
            <DefaultDialogTitle title={indexedParameters[fields.parameter]?.labelWithCode ?? ''} onClose={onClose} />
            <DialogContent>
                <StyledFieldSet>
                    <Grid2 container spacing={1}>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.unit}
                                value={unit}
                                options={units}
                                onChange={setUnit}
                                keyLabel='labelWithCode'
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.fraction}
                                value={fraction}
                                options={fractions}
                                onChange={setFraction}
                                keyLabel='labelWithCode'
                                keyValue='id'
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.support}
                                value={support}
                                options={supports}
                                onChange={setSupport}
                                keyLabel='labelWithCode'
                                keyValue='id'
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.place}
                                options={analysisPlaces}
                                onChange={setPlace}
                                value={place}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                    </Grid2>
                </StyledFieldSet>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onValidate({ unit, fraction, support, place })}
                    variant='contained'
                    color='primary'
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

UpdatePopin.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    fields: PropTypes.shape({
        parameter: PropTypes.string,
        unit: PropTypes.string,
        fraction: PropTypes.string,
        support: PropTypes.string,
        place: PropTypes.string,
    }),
}

const ParametersPanel = ({
    selectedParameters = [],
    selectedSelections = [],
    editMode = false,

    onDeleteParameter = () => { },
    onUpdateParameter = () => { },
}) => {
    const {
        parameters,
        selectionsWithParam,
        units,
        fractions,
        supports,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        selectionsWithParam: store.ParameterReducer.selectionsWithParam,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const indexedUnits = useListIndexed(units, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')
    const indexedSupports = useListIndexed(supports, 'code')
    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedSelections = useListIndexed(selectionsWithParam, 'code')

    const formattedSelections = useMemo(() =>
        selectedSelections.map(s => indexedSelections[s])
    , [selectedSelections, indexedSelections])

    const types = useMemo(getParametersType, [])
    const natures = useMemo(getParametersNature, [])

    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)
    const [selectedParam, setSelectedParam] = useState()
    const [selectedSelection, setSelectedSelection] = useState()

    const lineActions = (selection) => [{
        icon: 'close',
        onClick: (_, index) => {
            setSelectedSelection(selection)
            onDeleteParameter(index)
        },
        displayed: editMode,
    }, {
        icon: 'edit',
        onClick: (_, index) => {
            setSelectedSelection(selection)
            setSelectedParam(index)
            onOpen()
        },
        displayed: editMode,
    }]

    const formattedSelectionsWithParameters = useMemo(() => {
        const filteredParameters = selectedParameters.filter(p => indexedParameters[p.parameterCode]?.active)
        const parametersOutOfSelections = filteredParameters.filter(fP =>
            formattedSelections
                .map(s => !s?.parameters?.includes(fP.parameterCode))
                ?.reduce((doesNotInclude, curr) => doesNotInclude && curr, true))
        return [
            ...formattedSelections,
            {
                name: i18n.noSelections,
                parameters: parametersOutOfSelections.map(({ parameterCode }) => parameterCode),
            },
        ].map(s => {
            const formattedParams = compact(s?.parameters?.map(pCode => {
                const p = filteredParameters.find(fP => fP.parameterCode === pCode)
                if (!p) return undefined
                const parameter = indexedParameters[p.parameterCode]
                return {
                    code: parameter.code,
                    name: parameter.name,
                    type: types[parameter.typeParam],
                    nature: natures[parameter.nature],
                    status: parameter.status ? getStatusInformation(parameter.status) : i18n.validated,
                    unit: indexedUnits[p.unitCode]?.labelWithCode,
                    fraction: indexedFractions[p.fractionCode]?.labelWithCode,
                    support: indexedSupports[p.supportCode]?.labelWithCode,
                    place: getLocalizationPicto(p.placeCode),

                    data: {
                        parameter: p.parameterCode,
                        unit: p.unitCode,
                        fraction: p.fractionCode,
                        support: p.supportCode,
                        place: p.placeCode,
                    },
                }
            }))
            return { ...s, parameters: formattedParams }
        })
    }, [indexedFractions, indexedParameters, indexedSupports, indexedUnits,
        natures, selectedParameters, types, formattedSelections])

    return (
        <Stack spacing={1}>
            {formattedSelectionsWithParameters.map((selection, i) => (
                <Accordion key={i}>
                    <AccordionTitle
                        title={`${selection.name} (${selection?.parameters?.length || 0} ${getI18nTitleDataLength(i18n.parameter, i18n.parameters, selection?.parameters?.length || 0)})`}
                    />
                    <NewTable
                        rows={selection.parameters}
                        headers={headers}
                        rowsPerPageOptions={nbPerPageLabel}
                        lineActions={lineActions(i)}
                    />
                </Accordion>
            ))
            }
            <UpdatePopin
                isOpen={isOpen}
                onClose={onClose}
                onValidate={(changes) => {
                    const newParam = {
                        parameterCode: formattedSelectionsWithParameters[selectedSelection]?.parameters[selectedParam]?.code,
                        unitCode: changes.unit,
                        fractionCode: changes.fraction,
                        supportCode: changes.support,
                        placeCode: isNil(changes.place) ? undefined : `${changes.place}`,
                    }
                    onUpdateParameter(selectedParam, newParam)
                    onClose()
                }}

                fields={formattedSelectionsWithParameters[selectedSelection]?.parameters[selectedParam]?.data}
            />
        </Stack>
    )
}

ParametersPanel.propTypes = {
    selectedParameters: PropTypes.arrayOf(PropTypes.shape({
        parameterCode: PropTypes.string,
        fractionCode: PropTypes.string,
        supportCode: PropTypes.string,
        unitCode: PropTypes.string,
        placeCode: PropTypes.string,
    })),
    selectedSelections: PropTypes.arrayOf(PropTypes.shape({})),
    onDeleteParameter: PropTypes.func,
    onUpdateParameter: PropTypes.func,
    editMode: PropTypes.bool,
}

export default ParametersPanel
